import React from 'react';
import { Box, useTheme } from '@mui/material';

import { Text } from 'components';
import { formatDate, formatPrice } from 'core/shared';
import { useHasMobileView } from 'hooks/useHasMobileView';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { UseQuery } from 'useQuery';
import { InvoiceBlock } from 'useQuery/types';

import { InvoiceLinesDialog } from './InvoiceLinesDialog';
import { formatPeriod, InvoiceStatusChip } from './InvoicingShared';

interface OrganisationInvoicesProps {
  organisationId: string;
}

const COLUMNS = {
  status: 84,
  period: 148,
  date: 102,
  reference: 240,
  subscription: 96,
  amount: 80,
  total: 92,
};

// Combined view of generated rows (invoiced once pushed to Exact, draft while
// they are not) and scheduled rows (projections from the saved settings),
// oldest first, from GET /admin/organisation/{id}/invoices?show_scheduled=true.
const OrganisationInvoices = React.forwardRef<HTMLDivElement, OrganisationInvoicesProps>(
  function OrganisationInvoices({ organisationId }, ref) {
    const theme = useTheme();
    const { hasMobileView } = useHasMobileView();
    const translations = useTranslations('organisationInvoicing');

    const { data: invoicesData } = UseQuery.organisationInvoices({ id: organisationId });

    // Kept after closing so the pop-up keeps its content while it fades out.
    const [selectedInvoice, setSelectedInvoice] = React.useState<InvoiceBlock | null>(null);
    const [linesOpen, setLinesOpen] = React.useState(false);

    const rows = invoicesData?.invoices ?? [];
    const scheduled = rows.filter((invoice) => invoice.status === 'scheduled');

    function HeaderCell({
      label,
      width,
      flex,
      alignRight,
    }: {
      label: CustomString;
      width?: number;
      flex?: boolean;
      alignRight?: boolean;
    }) {
      return (
        <Box
          sx={{
            width,
            flex: flex ? 1 : undefined,
            minWidth: flex ? width : undefined,
            flexShrink: 0,
            textAlign: alignRight ? 'right' : undefined,
          }}
        >
          <Text.Bold>{label}</Text.Bold>
        </Box>
      );
    }

    function Cell({
      value,
      width,
      flex,
      alignRight,
      bold,
      gray,
    }: {
      value: string;
      width?: number;
      flex?: boolean;
      alignRight?: boolean;
      bold?: boolean;
      gray?: boolean;
    }) {
      return (
        <Box
          sx={{
            width,
            flex: flex ? 1 : undefined,
            minWidth: flex ? width : undefined,
            flexShrink: 0,
            textAlign: alignRight ? 'right' : undefined,
          }}
        >
          {bold ? (
            <Text.Bold color={gray ? 'gray' : undefined}>{value}</Text.Bold>
          ) : (
            <Text color={gray ? 'gray' : undefined}>{value}</Text>
          )}
        </Box>
      );
    }

    function openLines(invoice: InvoiceBlock) {
      setSelectedInvoice(invoice);
      setLinesOpen(true);
    }

    function InvoiceRow({ invoice }: { invoice: InvoiceBlock }) {
      const isScheduled = invoice.status === 'scheduled';
      return (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => {
            openLines(invoice);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openLines(invoice);
            }
          }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            paddingY: 1,
            paddingX: 1,
            marginX: -1,
            borderBottom: 1,
            borderColor: 'divider',
            // Faint tint marks projections apart from invoiced facts.
            backgroundColor: isScheduled ? 'action.hover' : undefined,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.selected' },
          }}
        >
          <Box sx={{ width: COLUMNS.status, flexShrink: 0 }}>
            <InvoiceStatusChip status={invoice.status} />
          </Box>
          <Cell
            value={formatPeriod(invoice.period_start, invoice.period_end) || translations.noValue}
            width={COLUMNS.period}
            gray={!invoice.period_start}
          />
          <Cell
            value={formatDate({ date: invoice.invoice_date, variant: 'dayMonthYear' })}
            width={COLUMNS.date}
          />
          <Cell
            value={invoice.reference ?? translations.noValue}
            width={COLUMNS.reference}
            flex
            gray={!invoice.reference}
          />
          <Cell
            value={formatPrice({ price: invoice.subscription_net, symbol: true })}
            width={COLUMNS.subscription}
            alignRight
          />
          <Cell
            value={formatPrice({ price: invoice.usage_net, symbol: true })}
            width={COLUMNS.amount}
            alignRight
          />
          <Cell
            value={formatPrice({ price: invoice.other_net, symbol: true })}
            width={COLUMNS.amount}
            alignRight
          />
          <Cell
            value={formatPrice({ price: invoice.net_total, symbol: true })}
            width={COLUMNS.total}
            alignRight
            bold
          />
        </Box>
      );
    }

    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: 'background.paper',
          padding: 2,
          marginBottom: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          maxWidth: `calc(100vw - ${theme.spacing(hasMobileView ? 4 : 6)})`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box>
            <Text.Bold>{translations.invoicesTitle}</Text.Bold>
            <Box sx={{ paddingTop: 0.5 }}>
              <Text.Small color="gray">
                {`${translations.invoicesSubtitle} ${translations.linesHint}`}
              </Text.Small>
            </Box>
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <Text.Small color="gray">
              {replaceText({
                text: translations.scheduledCount,
                replaceValues: [scheduled.length],
              })}
            </Text.Small>
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto', paddingTop: 2 }}>
          <Box sx={{ minWidth: 976 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                paddingY: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <HeaderCell label={translations.statusHeader} width={COLUMNS.status} />
              <HeaderCell label={translations.periodHeader} width={COLUMNS.period} />
              <HeaderCell label={translations.dateHeader} width={COLUMNS.date} />
              <HeaderCell label={translations.referenceHeader} width={COLUMNS.reference} flex />
              <HeaderCell
                label={translations.subscriptionHeader}
                width={COLUMNS.subscription}
                alignRight
              />
              <HeaderCell label={translations.usageHeader} width={COLUMNS.amount} alignRight />
              <HeaderCell label={translations.otherHeader} width={COLUMNS.amount} alignRight />
              <HeaderCell label={translations.totalHeader} width={COLUMNS.total} alignRight />
            </Box>

            {rows.length === 0 && (
              <Box sx={{ paddingY: 2 }}>
                <Text color="gray">{translations.noScheduledInvoices}</Text>
              </Box>
            )}

            {rows.map((invoice) => (
              <InvoiceRow
                key={`${invoice.status}-${invoice.reference ?? invoice.invoice_date}`}
                invoice={invoice}
              />
            ))}
          </Box>
        </Box>

        {/* Do not drop: scheduled rows are projections, not facts. */}
        {scheduled.length > 0 && (
          <Box sx={{ paddingTop: 1.5 }}>
            <Text.Small color="gray">{translations.schedNote}</Text.Small>
          </Box>
        )}

        <InvoiceLinesDialog
          invoice={selectedInvoice}
          open={linesOpen}
          onClose={() => {
            setLinesOpen(false);
          }}
        />
      </Box>
    );
  },
);

export { OrganisationInvoices };
